import type { StrictOmit } from '@enmon/commons';
import type { IdentifyResponse } from '@enmon/commons/lib/enmon';

/**
 * Session storage key for storing identified user data during auth flow
 */
const IDENTIFIED_USER_KEY = 'auth:identified_user';

/**
 * Session storage key for storing password reset token after OTP verification
 */
const PASSWORD_RESET_TOKEN_KEY = 'auth:password_reset_token';

export interface StoredIdentifiedUser extends IdentifyResponse {
  timestamp: number;
}

/**
 * Store the identified user data in session storage
 * This persists the identification across page refreshes within the same session
 */
export const storeIdentifiedUser = (identifyResponse: IdentifyResponse) => {
  try {
    const data = { ...identifyResponse, timestamp: Date.now() };
    sessionStorage.setItem(IDENTIFIED_USER_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Failed to store identified user:', error);
  }
};

/**
 * Retrieve the identified user data from session storage
 */
export const getStoredIdentifiedUser = (): StoredIdentifiedUser => {
  const defaultIdentifiedUser: StoredIdentifiedUser = {
    identifier: '',
    methods: {
      emailPassword: false,
      oauthProviders: [],
      oneTimePassword: false,
    },
    secondaryFactors: {
      twoFactor: false,
    },
    timestamp: 0,
  };

  try {
    const stored = sessionStorage.getItem(IDENTIFIED_USER_KEY);

    // If no data is stored, return a default empty object
    if (!stored) return defaultIdentifiedUser;

    const data = JSON.parse(stored) as StoredIdentifiedUser;

    // Check if stored data is too old (e.g., older than 1 hour)
    const ONE_HOUR = 60 * 60 * 1000;
    if (Date.now() - data.timestamp > ONE_HOUR) {
      clearIdentifiedUser();
      return defaultIdentifiedUser;
    }

    return data;
  } catch (error) {
    console.error('Failed to get identified user:', error);
    return defaultIdentifiedUser;
  }
};

/**
 * Retrieve just the identifier from session storage (backward compatibility)
 */
export const getIdentifier = (): StoredIdentifiedUser['identifier'] | null => {
  return getStoredIdentifiedUser()?.identifier ?? null;
};

/**
 * Clear the identified user from session storage
 * Should be called on logout or when authentication is complete
 */
export const clearIdentifiedUser = () => {
  try {
    sessionStorage.removeItem(IDENTIFIED_USER_KEY);
  } catch (error) {
    console.error('Failed to clear identified user:', error);
  }
};

/**
 * Check if user has been identified (either in memory or session storage)
 */
export const hasIdentifiedUser = (identifierFromState?: StoredIdentifiedUser['identifier']) => {
  return Boolean(identifierFromState || getIdentifier());
};

/**
 * Check if user has a specific authentication method available
 */
export const hasAuthMethod = (
  method: keyof StrictOmit<StoredIdentifiedUser['methods'], 'oauthProviders'>,
  methodsFromState?: Pick<
    StoredIdentifiedUser['methods'],
    Exclude<keyof StoredIdentifiedUser['methods'], 'oauthProviders'>
  >,
) => {
  // First check state
  if (methodsFromState?.[method]) {
    return true;
  }

  // Fall back to session storage
  const stored = getStoredIdentifiedUser();
  return stored?.methods?.[method] ?? false;
};

/**
 * Store password reset token in session storage
 * Used after OTP verification for users who need to set their password
 */
export const storePasswordResetToken = (token: string) => {
  try {
    sessionStorage.setItem(PASSWORD_RESET_TOKEN_KEY, token);
  } catch (error) {
    console.error('Failed to store password reset token:', error);
  }
};

/**
 * Retrieve password reset token from session storage
 */
export const getPasswordResetToken = (): string | null => {
  try {
    return sessionStorage.getItem(PASSWORD_RESET_TOKEN_KEY);
  } catch (error) {
    console.error('Failed to get password reset token:', error);
    return null;
  }
};

/**
 * Clear password reset token from session storage
 * Should be called after password is set or on error
 */
export const clearPasswordResetToken = () => {
  try {
    sessionStorage.removeItem(PASSWORD_RESET_TOKEN_KEY);
  } catch (error) {
    console.error('Failed to clear password reset token:', error);
  }
};
